import { ACTOR_TRIGGERS, NOTIFIABLE_CATEGORIES, OFFICE_OPEN_TRIGGERS, OUT_OF_HOURS_RULES, type NotifiableCategory, type OutOfHours } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { ChoiceChips, Field, Select, Switch } from "@sagot/ui";
import { useContext } from "react";
import type { NotifyTeamStep } from "../../types";
import { TriggerContext } from "../hook-model";
import type { FormProps } from "../webhook-form";
import { NotifyMessage } from "./notify-message";
import { NotifyRecipients } from "./notify-recipients";

const N = "engage.workflows.system.notify";
const CHANNELS = ["in_app", "email"] as const;

/** Notify team members: who is told, how it reaches them, and the wording (the standard message, or the workspace's own). */
export function NotifyTeamForm({ step, disabled, onChange }: FormProps<NotifyTeamStep>) {
  const { t } = useTranslation();
  const trigger = useContext(TriggerContext);
  const knowsActor = trigger !== undefined && ACTOR_TRIGGERS.has(trigger);
  const hasOfficeClock = trigger !== undefined && OFFICE_OPEN_TRIGGERS.has(trigger);
  return (
    <div className="space-y-5">
      <Field label={t(`${N}.category`)} hint={t(`${N}.categoryHint`)}>
        <Select value={step.category} disabled={disabled} onChange={(e) => { onChange({ ...step, category: e.target.value as NotifiableCategory }); }}>
          {NOTIFIABLE_CATEGORIES.map((category) => <option key={category} value={category}>{t(`${N}.categories.${category}`)}</option>)}
        </Select>
      </Field>
      <NotifyRecipients recipients={step.to} disabled={disabled} onChange={(to) => { onChange({ ...step, to }); }} />
      {knowsActor ? (
        <div className="flex items-center justify-between gap-4 rounded-md bg-surface-2 px-3 py-2.5">
          <span id={`${step.id}-actor`} className="text-sm font-semibold">{t(`${N}.excludeActor`)}</span>
          <Switch checked={step.exclude_actor === true} disabled={disabled} onCheckedChange={(exclude_actor) => { onChange({ ...step, exclude_actor }); }} aria-labelledby={`${step.id}-actor`} />
        </div>
      ) : null}
      <div>
        <span className="label">{t(`${N}.channels`)}</span>
        {step.channels_from ? (
          <p className="text-xs text-fg-muted">{t(`${N}.channelsFromSource`)}</p>
        ) : (
          <>
            <ChoiceChips
              multiple
              aria-label={t(`${N}.channels`)}
              value={step.channels ?? []}
              onValueChange={(picked) => {
                const channels = CHANNELS.filter((channel) => picked.includes(channel));
                onChange({ ...step, channels: channels.length > 0 ? channels : undefined });
              }}
              options={CHANNELS.map((channel) => ({ value: channel, label: t(`${N}.channelOptions.${channel}`), disabled }))}
            />
            <p className="mt-1 text-xs text-fg-subtle">{t(step.channels ? `${N}.channelsCapped` : `${N}.channelsAll`)}</p>
          </>
        )}
      </div>
      {hasOfficeClock ? (
        <Field label={t(`${N}.outOfHours`)} hint={t(`${N}.outOfHoursHint`)}>
          <Select value={step.out_of_hours ?? "same"} disabled={disabled} onChange={(e) => { onChange({ ...step, out_of_hours: e.target.value as OutOfHours }); }}>
            {OUT_OF_HOURS_RULES.map((rule) => <option key={rule} value={rule}>{t(`${N}.outOfHoursRules.${rule}`)}</option>)}
          </Select>
        </Field>
      ) : null}
      <NotifyMessage step={step} trigger={trigger} disabled={disabled} onChange={onChange} />
    </div>
  );
}
